/**
 * 🔔 Notification Service — Application Layer
 *
 * يتعامل مع الـ notifications collection:
 *   - عرض إشعارات المستخدم
 *   - تحديد كمقروء
 *   - real-time stream
 *   - حذف
 *
 * الـ notifications تُولّد من Cloud Functions تلقائياً
 * (booking events, provider approval, scheduled tasks)
 */

import {
  collection,
  doc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  writeBatch,
  getDocs,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '@infrastructure/firebase/config';
import { useAuthStore } from '@application/stores/auth.store';
import { bookingService } from '@application/services/BookingService';
import type { Booking } from '@domain/entities/Booking';
import { ACTIVE_REQUEST_STATUSES } from '@domain/value-objects/BookingRequestStatus';

/**
 * 📡 مصدر الإشعار:
 *   - 'firestore' : مستند حقيقي في notifications collection (تكتبه Cloud Functions).
 *                    حالة القراءة/الحذف تُحفظ في المستند نفسه.
 *   - 'booking'   : مُشتقّ حيّ من booking_requests (client-side). لا مستند خلفه —
 *                    حالة "مقروء/مُخفى" تُحفظ في localStorage (seen-set).
 */
export type NotificationSource = 'firestore' | 'booking';

export interface AppNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  relatedId?: string | undefined;
  route?: string | undefined;
  isRead: boolean;
  createdAt: Date;
  /** مصدر الإشعار — يحدّد كيف تُدار حالة القراءة/الحذف */
  source: NotificationSource;
}

/** بادئة معرّف الإشعارات المُشتقّة من الحجوزات (لتمييزها عن مستندات firestore) */
const BOOKING_NOTIF_PREFIX = 'booking:';

/** مفتاح تخزين الحجوزات المقروءة/المُخفاة محلياً (لكل مستخدم) */
function seenStorageKey(uid: string): string {
  return `havlaa:notif-booking-seen:${uid}`;
}

/**
 * 🔗 تطبيع مسار الإشعار قبل التنقّل.
 *
 * الإشعارات القديمة محفوظ فيها المسار بصيغة path segment
 * (`/bookings/{id}`) وهذا المسار غير معرّف في الراوتر ولا يفتح
 * الحجز. صفحة الحجوزات تفتح الـ modal عبر query param
 * (`/bookings?bookingId={id}`)، فنحوّل الصيغة القديمة إليها.
 * (الإشعارات الجديدة تُكتب مسبقاً بالصيغة الصحيحة من الـ backend.)
 */
export function normalizeNotificationRoute(route: string): string {
  const match = route.match(/^\/bookings\/([^/?#]+)$/);
  if (match) return `/bookings?bookingId=${match[1]}`;
  return route;
}

class NotificationService {
  /**
   * 🔴 Live stream — كل إشعارات المستخدم
   *
   * ⚠️ client-side sort + slice لتجنّب composite index requirement
   */
  subscribe(
    callback: (notifications: AppNotification[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    const user = useAuthStore.getState().user;
    if (!user) {
      callback([]);
      return () => {};
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid)
    );

    return onSnapshot(
      q,
      (snap) => {
        const notifications = snap.docs
          .map((d) => fromFirestore(d.id, d.data()))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 50);
        callback(notifications);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[NotificationService] stream error:', err);
        onError?.(err);
      }
    );
  }

  /**
   * 🔴 Live stream — إشعارات مُشتقّة من الحجوزات (client-side)
   *
   * يستخدم نفس مصدر قائمة الحجوزات الحيّ (bookingService.subscribeMyBookings):
   *   - Admin/super_admin → كل الحجوزات
   *   - Provider/Vendor  → حجوزاته فقط (نفس منطق الصلاحيات)
   *
   * يحوّل كل حجز إلى AppNotification "طلب جديد ينتظرك" (type: booking_created).
   * لا مستند firestore خلف هذه الإشعارات — حالة "مقروء/مُخفى" تُحفظ في
   * localStorage (seen-set)، فتبقى ثابتة عبر إعادة تحميل الصفحة.
   *
   * ⚠️ هذا يحلّ مشكلة أن Cloud Function تكتب الإشعار بـ userId = صاحب القاعة،
   *    فلا يظهر في جرس الأدمن (المفلتر بـ uid الأدمن). هنا الجرس يقرأ من نفس
   *    مصدر قائمة الحجوزات مباشرة — فيظهر لكل من يرى الحجز.
   */
  subscribeBookingDerived(
    callback: (notifications: AppNotification[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    const user = useAuthStore.getState().user;
    if (!user) {
      callback([]);
      return () => {};
    }

    return bookingService.subscribeMyBookings(
      (bookings) => {
        const seen = this.getSeenBookings(user.uid);
        const notifications = bookings
          .map((b) => bookingToNotification(b, seen, user.uid))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 50);
        callback(notifications);
      },
      onError,
      // طلبات نشطة فقط (pending/contacted) — تحتاج متابعة. لا نشعر بالمحسومة.
      { status: [...ACTIVE_REQUEST_STATUSES] }
    );
  }

  /**
   * 👁️ حدّد حجزاً كـ "مقروء/مُخفى" محلياً (seen-set في localStorage)
   */
  markBookingSeen(bookingId: string): void {
    const user = useAuthStore.getState().user;
    if (!user) return;
    const seen = this.getSeenBookings(user.uid);
    // delete-then-add: ينقل المعرّف لآخر الترتيب (recency) قبل slice(-500)
    seen.delete(bookingId);
    seen.add(bookingId);
    this.persistSeenBookings(user.uid, seen);
  }

  /**
   * 👁️👁️ حدّد عدة حجوزات كـ "مقروءة" دفعة واحدة
   */
  markBookingsSeen(bookingIds: string[]): void {
    const user = useAuthStore.getState().user;
    if (!user || bookingIds.length === 0) return;
    const seen = this.getSeenBookings(user.uid);
    for (const id of bookingIds) {
      seen.delete(id);
      seen.add(id);
    }
    this.persistSeenBookings(user.uid, seen);
  }

  /**
   * 🔍 معرّفات الحجوزات المُعلَّمة "مقروءة/مُخفاة" للمستخدم الحالي.
   *   يُستخدم لإعادة اشتقاق حالة القراءة عند دمج المصادر (تفادي إحياء
   *   إشعار محذوف/مقروء من نسخة قديمة مخزّنة في الذاكرة).
   */
  getSeenBookingIds(): Set<string> {
    const user = useAuthStore.getState().user;
    if (!user) return new Set();
    return this.getSeenBookings(user.uid);
  }

  // ─── seen-set helpers (localStorage) ───

  private getSeenBookings(uid: string): Set<string> {
    try {
      const raw = localStorage.getItem(seenStorageKey(uid));
      if (!raw) return new Set();
      const arr = JSON.parse(raw) as unknown;
      return Array.isArray(arr)
        ? new Set(arr.filter((x): x is string => typeof x === 'string'))
        : new Set();
    } catch {
      return new Set();
    }
  }

  private persistSeenBookings(uid: string, seen: Set<string>): void {
    try {
      // نحدّ الحجم (آخر 500) لتجنّب تضخّم localStorage بلا حدود
      const arr = Array.from(seen).slice(-500);
      localStorage.setItem(seenStorageKey(uid), JSON.stringify(arr));
    } catch {
      // localStorage ممتلئ أو غير متاح — نتجاهل (الحالة تبقى in-memory فقط)
    }
  }

  /**
   * 🌐 (Admin only) Live stream لكل إشعارات النظام
   *   - يتطلب صلاحية admin (Firestore Rules تتحقق)
   *   - مفيد لـ "Admin Notifications Center"
   */
  subscribeAllSystem(
    callback: (notifications: AppNotification[]) => void,
    onError?: (err: Error) => void,
    limitN: number = 200
  ): () => void {
    const user = useAuthStore.getState().user;
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      callback([]);
      return () => {};
    }

    const q = query(collection(db, 'notifications'));

    return onSnapshot(
      q,
      (snap) => {
        const notifications = snap.docs
          .map((d) => fromFirestore(d.id, d.data()))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, limitN);
        callback(notifications);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[NotificationService] system stream error:', err);
        onError?.(err);
      }
    );
  }

  /**
   * 📊 العدد غير المقروءة
   */
  async getUnreadCount(): Promise<number> {
    const user = useAuthStore.getState().user;
    if (!user) return 0;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('isRead', '==', false)
    );
    const snap = await getDocs(q);
    return snap.size;
  }

  /**
   * ✅ تحديد كمقروء
   */
  async markAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(db, 'notifications', notificationId), {
      isRead: true,
      readAt: serverTimestamp(),
    });
  }

  /**
   * ✅✅ تحديد الكل كمقروء
   */
  async markAllAsRead(): Promise<number> {
    const user = useAuthStore.getState().user;
    if (!user) return 0;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('isRead', '==', false)
    );
    const snap = await getDocs(q);

    const batch = writeBatch(db);
    for (const d of snap.docs) {
      batch.update(d.ref, {
        isRead: true,
        readAt: serverTimestamp(),
      });
    }
    await batch.commit();
    return snap.size;
  }

  /**
   * ✅✅ (Admin only) تحديد إشعارات نظام محدّدة كمقروءة دفعة واحدة.
   *
   *   ⚠️ صفحة "مركز إشعارات النظام" تعرض إشعارات كل المستخدمين
   *      (subscribeAllSystem)، بينما markAllAsRead أعلاه مقيّدة بـ uid الأدمن
   *      نفسه فقط. لذا نمرّر هنا معرّفات المستندات الظاهرة صراحةً بدل الاعتماد
   *      على where(userId).
   *
   *   - يتطلب صلاحية admin (Firestore Rules تتحقق).
   *   - يقسّم على دفعات (Firestore batch = 500 عملية كحد أقصى).
   *   - يُرجع عدد ما حُدّث.
   */
  async markSystemNotificationsAsRead(
    notificationIds: string[]
  ): Promise<number> {
    const user = useAuthStore.getState().user;
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      return 0;
    }
    if (notificationIds.length === 0) return 0;

    const CHUNK = 450; // < 500 (حد batch)
    let updated = 0;
    for (let i = 0; i < notificationIds.length; i += CHUNK) {
      const chunk = notificationIds.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const id of chunk) {
        batch.update(doc(db, 'notifications', id), {
          isRead: true,
          readAt: serverTimestamp(),
        });
      }
      await batch.commit();
      updated += chunk.length;
    }
    return updated;
  }

  /**
   * 🗑️ حذف إشعار
   */
  async delete(notificationId: string): Promise<void> {
    await deleteDoc(doc(db, 'notifications', notificationId));
  }
}

export const notificationService = new NotificationService();

// ─── Helper ───
function fromFirestore(id: string, data: Record<string, unknown>): AppNotification {
  return {
    id,
    userId: data['userId'] as string,
    type: (data['type'] as string) ?? 'general',
    title: (data['title'] as string) ?? '',
    body: (data['body'] as string) ?? '',
    relatedId: data['relatedId'] as string | undefined,
    route: data['route'] as string | undefined,
    isRead: (data['isRead'] as boolean) ?? false,
    createdAt: parseDate(data['createdAt']),
    source: 'firestore',
  };
}

/**
 * 🔄 يحوّل حجزاً إلى AppNotification "طلب جديد ينتظرك".
 *   - المعرّف: `booking:{bookingId}` (مميّز عن مستندات firestore)
 *   - isRead: من seen-set المحلي
 *   - النص يطابق Cloud Function onBookingCreated
 */
function bookingToNotification(
  booking: Booking,
  seen: Set<string>,
  viewerUid: string
): AppNotification {
  const p = booking.props;
  const customerName = p.customer?.name || 'عميل';
  const venueName = p.venueName || 'قاعة';

  return {
    id: `${BOOKING_NOTIF_PREFIX}${booking.id}`,
    userId: viewerUid,
    type: 'booking_created',
    title: '🎉 طلب جديد ينتظرك',
    body: `${customerName} مهتم بـ ${venueName} بتاريخ ${p.bookingDate}. تواصل معه لمتابعة الطلب.`,
    relatedId: booking.id,
    route: `/bookings?bookingId=${booking.id}`,
    isRead: seen.has(booking.id),
    createdAt: p.createdAt,
    source: 'booking',
  };
}

/**
 * 🔎 هل هذا الإشعار مُشتقّ من حجز؟ (مقابل مستند firestore حقيقي)
 */
export function isBookingNotification(n: AppNotification): boolean {
  return n.source === 'booking';
}

function parseDate(value: unknown): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
}
